/**
 * @module lib/auth
 * @summary Client-side accounts, roles and session.
 * @description
 *   When VITE_WORKER_URL is set, the Worker API is the source of truth.
 *   localStorage acts as a cache so the app still works offline.
 *   Credentials are stored in sessionStorage for authenticated API writes.
 *
 *   NOTE: This is a UX gate. Passwords travel over HTTPS but are NOT hashed.
 *   Suitable for an internal company intranet tool, not for public-facing auth.
 */
import { KEYS, SESSION_KEY, lsGet, lsSet, lsRemove } from './storage';
import {
  apiLogin,
  apiGetUsers,
  apiAddUser,
  apiDeleteUser,
  apiSetPassword,
  setApiCreds,
  clearApiCreds,
  getApiCreds,
} from './api';
import type { User, Session, Role } from '../types';

const DEFAULT_USERS: User[] = [
  { username: 'JCCsuperadmin', password: 'admin123', role: 'superadmin', createdAt: '' },
];

export const ROLE_LABELS: Record<Role, string> = {
  engineer: 'Engineer',
  manager: 'Manager',
  superadmin: 'Super Admin',
};

export const ROLE_COLORS: Record<Role, string> = {
  engineer: '#005a9e',
  manager: '#107c10',
  superadmin: '#7a3a00',
};

/** Load accounts from localStorage (seeding the default superadmin on first run). */
export function getUsers(): User[] {
  const users = lsGet<User[]>(KEYS.users, []);
  if (!users.length) {
    lsSet(KEYS.users, DEFAULT_USERS);
    return DEFAULT_USERS.slice();
  }
  return users;
}

/** Fetch the latest user list from the API and update the local cache. */
export async function syncUsersFromApi(): Promise<User[]> {
  const creds = getApiCreds();
  if (!creds) return getUsers();
  const remote = await apiGetUsers(creds);
  if (!remote.length) return getUsers();
  // The server list wins outright. An earlier version re-inserted a cached
  // JCCsuperadmin whenever the server didn't return one, which meant deleting
  // that account only held until the next browser with it cached saved again.
  lsSet(KEYS.users, remote);
  return remote;
}

/** The current session from sessionStorage, or null. */
export function getCurrentUser(): Session | null {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null') as Session | null;
  } catch {
    return null;
  }
}

export function setCurrentUser(u: Session): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(u));
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  clearApiCreds();
  // Drop the cached account list. A superadmin's sync pulls down every user's
  // password, and on a shared PC that outlives their session otherwise. The
  // list is re-fetched from the Worker at the next sign-in.
  lsRemove(KEYS.users);
}

/**
 * Attempt a login. Checks the Worker API first (if configured), then falls
 * back to the localStorage user list.
 */
export async function tryLogin(username: string, password: string): Promise<Session | null> {
  // 1. Try the Worker API.
  const apiRole = await apiLogin(username, password);
  if (apiRole) {
    setApiCreds(username, password);
    const session: Session = { username, role: apiRole };
    setCurrentUser(session);
    return session;
  }

  // 2. Fall back to localStorage (works offline / before Worker is deployed).
  const u = getUsers().find((x) => x.username === username && x.password === password);
  if (!u) return null;
  setApiCreds(username, password); // store creds even for local login
  const session: Session = { username: u.username, role: u.role };
  setCurrentUser(session);
  return session;
}

/**
 * Account changes go through the Worker's atomic routes, which re-read the
 * stored list before applying one change, so two superadmins editing at once
 * can't overwrite each other. `null` means the Worker accepted it; a string is
 * an error to show. When the Worker is unreachable each falls back to editing
 * the local cache so the tool still works offline.
 */

/** Add a new account. */
export async function addUser(
  username: string,
  password: string,
  role: Role,
): Promise<string | null> {
  const uname = username.trim();
  if (!uname) return 'Username is required.';
  if (!password) return 'Password is required.';
  if (!/^[a-zA-Z0-9._\-@]+$/.test(uname)) return 'Username may only contain letters, numbers, . _ - @';

  const creds = getApiCreds();
  if (creds) {
    const res = await apiAddUser({ username: uname, password, role, createdAt: '' }, creds);
    if (res.error) return res.error;
    if (res.users) {
      lsSet(KEYS.users, res.users);
      return null;
    }
  }

  const users = getUsers();
  if (users.find((u) => u.username === uname)) return 'Username already exists.';
  users.push({ username: uname, password, role, createdAt: new Date().toISOString() });
  lsSet(KEYS.users, users);
  return null;
}

/** Delete an account by username. */
export async function deleteUser(username: string): Promise<string | null> {
  const creds = getApiCreds();
  if (creds) {
    const res = await apiDeleteUser(username, creds);
    if (res.error) return res.error;
    if (res.users) {
      lsSet(KEYS.users, res.users);
      return null;
    }
  }
  lsSet(KEYS.users, getUsers().filter((u) => u.username !== username));
  return null;
}

/** Reset an account's password. */
export async function resetPassword(
  username: string,
  newPassword: string,
): Promise<string | null> {
  if (!newPassword) return 'Password cannot be empty.';

  const creds = getApiCreds();
  if (creds) {
    const res = await apiSetPassword(username, newPassword, creds);
    if (res.error) return res.error;
    if (res.users) {
      lsSet(KEYS.users, res.users);
      return null;
    }
  }

  const users = getUsers();
  const u = users.find((x) => x.username === username);
  if (!u) return 'User not found.';
  u.password = newPassword;
  lsSet(KEYS.users, users);
  return null;
}
